import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, ArrowRight, Clock, Coins, Sparkles, Plus, Loader2, AlertCircle, X, Mail, Globe, MapPin } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import DraggablePopularTag from './DraggablePopularTag';
import { useLanguage, translations } from '../i18nContext';
import {
  purchaseMembership,
  purchaseDurationPackage,
  confirmMembershipPurchase,
  confirmDurationPackagePurchase,
  getPaymentRegion,
  PaymentRegionInfo,
} from '../services/membership';
import { copyToClipboard } from '../utils/clipboard';
import { getErrorMessage } from '../utils/error';

export default function Pricing({ onSignUp }: { onSignUp?: () => void }) {
  const [isAnnual, setIsAnnual] = useState(true);
  const { t } = useLanguage();

  // 购买状态
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 地区检测弹窗状态
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [regionInfo, setRegionInfo] = useState<PaymentRegionInfo | null>(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<{ type: 'membership' | 'duration'; value: string } | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);

  // 二维码弹窗状态
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [qrOrderNo, setQrOrderNo] = useState<string | null>(null);
  const [qrPaymentType, setQrPaymentType] = useState<string | null>(null);

  // 价格地区状态（根据IP检测，独立于UI语言）
  const [paymentRegion, setPaymentRegion] = useState<string>('CN');

  // 清理定时器
  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  // 根据IP地区选择价格和币种（独立于UI语言）
  const regionPricing = paymentRegion === 'OVERSEAS' ? translations.en.pricing : translations.zh.pricing;
  const regionPrices = regionPricing.prices;
  const regionCurrency = regionPricing.currencySymbol;

  // 动态计算锚点价格（每小时单价及节省比例）
  const calculatePriceAnchor = () => {
    const singlePrice = parseFloat(regionPrices.addons.single);
    const monthlyPrice = parseFloat(regionPrices.pro.monthly);
    const yearlyPrice = parseFloat(regionPrices.pro.yearly);
    const hoursPerMonth = 1000 / 60;

    const currentPrice = isAnnual ? yearlyPrice : monthlyPrice;
    const hourlyRate = (currentPrice / hoursPerMonth).toFixed(1);
    const savingsPercent = Math.round((1 - (currentPrice / hoursPerMonth) / singlePrice) * 100);

    return `${t.pricing.priceAnchor.split(/[\d.]+/)[0]}${regionCurrency}${hourlyRate}${t.pricing.priceAnchor.includes('/') ? t.pricing.priceAnchor.substring(t.pricing.priceAnchor.indexOf('/'), t.pricing.priceAnchor.indexOf('(') - 1) : ''} (${savingsPercent}%)`;
  };

  // 检测IP地区，决定价格币种
  useEffect(() => {
    getPaymentRegion().then(info => {
      setPaymentRegion(info.region);
    }).catch(() => { });
  }, []);

  // 关闭地区弹窗的统一方法
  const closeRegionModal = useCallback(() => {
    setShowRegionModal(false);
    setPendingPurchase(null);
    setSelectedPaymentMethod(null);
  }, []);

  // 安全提交支付宝表单（DOMParser 白名单校验）
  const submitAlipayForm = (payFormHtml: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(payFormHtml, 'text/html');
    const form = doc.querySelector('form');
    if (!form) return false;
    const action = form.getAttribute('action') || '';
    const allowedDomains = ['alipay.com', 'alipaydev.com'];
    try {
      const url = new URL(action);
      if (!allowedDomains.some(d => url.hostname.endsWith(d))) return false;
    } catch {
      return false;
    }
    const container = document.createElement('div');
    container.innerHTML = payFormHtml;
    document.body.appendChild(container);
    const liveForm = container.querySelector('form');
    if (liveForm) {
      liveForm.setAttribute('target', '_blank');
      liveForm.submit();
    }
    document.body.removeChild(container);
    return true;
  };

  // 处理支付响应的共享逻辑
  const handlePaymentResponse = async (
    response: { codeUrl?: string; orderNo: string; paymentType?: string; payForm?: string },
    confirmFn: (orderNo: string) => Promise<unknown>,
    successMessage: string
  ) => {
    if (response.codeUrl) {
      setQrCodeUrl(response.codeUrl);
      setQrOrderNo(response.orderNo);
      setQrPaymentType(response.paymentType || null);
      setShowQrModal(true);
    } else if (response.payForm) {
      submitAlipayForm(response.payForm);
      setPurchaseSuccess(t.paymentModal.alipayRedirect);
    } else {
      await confirmFn(response.orderNo);
      setPurchaseSuccess(successMessage);
    }
  };

  // 点击购买时先检测地区
  const handlePurchaseClick = async (type: 'membership' | 'duration', value: string) => {
    setPendingPurchase({ type, value });
    setRegionLoading(true);
    setShowRegionModal(true);
    try {
      const info = await getPaymentRegion();
      setRegionInfo(info);
    } catch {
      setRegionInfo(null);
    } finally {
      setRegionLoading(false);
    }
  };

  // 弹窗确认后执行真正的购买
  const handleRegionConfirm = () => {
    if (!selectedPaymentMethod) return;
    setShowRegionModal(false);
    if (!pendingPurchase) return;
    const method = selectedPaymentMethod;
    setSelectedPaymentMethod(null);
    if (pendingPurchase.type === 'membership') {
      handlePurchaseMembership(pendingPurchase.value as 'pro' | 'team', method);
    } else {
      handlePurchaseDurationPackage(pendingPurchase.value, method);
    }
    setPendingPurchase(null);
  };

  // 购买会员
  const handlePurchaseMembership = async (membershipType: 'pro' | 'team', paymentMethod: string) => {
    setPurchasing(membershipType);
    setPurchaseError(null);
    setPurchaseSuccess(null);

    try {
      const months = isAnnual ? 12 : 1;
      const response = await purchaseMembership({
        membershipType,
        months,
        isAnnual,
        paymentMethod,
      });

      const successMsg = membershipType === 'pro'
        ? t.paymentModal.purchaseSuccessPro
        : t.paymentModal.purchaseSuccessTeam;
      await handlePaymentResponse(response, confirmMembershipPurchase, successMsg);
    } catch (error: unknown) {
      setPurchaseError(getErrorMessage(error, t.paymentModal.purchaseFailedFallback));
    } finally {
      setPurchasing(null);
    }
  };

  // 购买时长包
  const handlePurchaseDurationPackage = async (packageCode: string, paymentMethod: string) => {
    setPurchasing(packageCode);
    setPurchaseError(null);
    setPurchaseSuccess(null);

    try {
      const response = await purchaseDurationPackage({ packageCode, paymentMethod });
      await handlePaymentResponse(response, confirmDurationPackagePurchase, t.paymentModal.purchaseSuccessDuration);
    } catch (error: unknown) {
      setPurchaseError(getErrorMessage(error, t.paymentModal.purchaseFailedFallback));
    } finally {
      setPurchasing(null);
    }
  };

  // Draggable tag uses DraggablePopularTag component

  return (
    <div className="w-full min-h-screen pt-10 pb-24 relative overflow-hidden bg-surface-DEFAULT font-sans text-brand-900">

      <div className="max-w-7xl mx-auto px-6 relative z-10">

        {(purchaseError || purchaseSuccess) && (
          <div className="fixed top-4 right-4 z-50 max-w-md animate-in slide-in-from-top-2 duration-300">
            {purchaseError && (
              <div className="bg-white border border-feedback-error/20 rounded-2xl p-4 shadow-lg flex items-start gap-3">
                <AlertCircle size={20} className="text-feedback-error shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-feedback-error">{t.paymentModal.purchaseFailed}</div>
                  <div className="text-xs text-feedback-error/80 mt-1">{purchaseError}</div>
                </div>
                <button onClick={() => setPurchaseError(null)} className="text-feedback-error/60 hover:text-feedback-error">×</button>
              </div>
            )}
            {purchaseSuccess && (
              <div className="bg-white border border-brand-200 rounded-2xl p-4 shadow-lg flex items-start gap-3">
                <Check size={20} className="text-brand-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-brand-900">{t.paymentModal.purchaseSuccess}</div>
                  <div className="text-xs text-ink-secondary mt-1">{purchaseSuccess}</div>
                </div>
                <button onClick={() => setPurchaseSuccess(null)} className="text-ink-muted hover:text-brand-900">×</button>
              </div>
            )}
          </div>
        )}

        <div className="mx-auto mb-6 max-w-3xl text-center">
          <div className="flex items-center justify-center">
            <button
              type="button"
              aria-label={isAnnual ? t.pricing.yearly : t.pricing.monthly}
              className="relative inline-flex h-14 w-full max-w-[430px] cursor-pointer rounded-full border border-surface-divider bg-white/80 p-1.5 shadow-nav backdrop-blur-xl"
              onClick={() => setIsAnnual(!isAnnual)}
            >
              <div
                className={`absolute top-1.5 bottom-1.5 rounded-full bg-brand-900 transition-all duration-500 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] shadow-lg ${isAnnual ? 'left-[calc(36%+6px)] right-1.5' : 'left-1.5 right-[calc(64%+6px)]'}`}
              ></div>

              <div className={`relative z-10 flex w-[36%] items-center justify-center text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${!isAnnual ? 'text-white' : 'text-ink-dark'}`}>
                {t.pricing.monthly}
              </div>

              <div className={`relative z-10 flex w-[64%] items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${isAnnual ? 'text-white' : 'text-ink-dark'}`}>
                {t.pricing.yearly}
                <span className={`relative overflow-hidden rounded-full px-2.5 py-1 text-[10px] font-black tracking-[0.12em] transition-all duration-300 shadow-sm ${isAnnual ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200/50' : 'bg-surface text-ink-muted border border-surface-border'}`}>
                  <span className="relative z-10">{t.pricing.save20}</span>
                  {isAnnual && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-200/40 to-transparent animate-shimmer-gold opacity-50 pointer-events-none w-[200%] h-full"></div>}
                </span>
              </div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
          <div className="group flex flex-col rounded-pricing bg-white/90 backdrop-blur-sm border border-surface-border p-8 lg:p-9 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover">
            <div className="min-h-[130px] space-y-2">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-ink-muted/50">Essential</div>
              <h3 className="text-4xl font-serif font-bold text-brand-900">{t.pricing.plans.basic.name}</h3>
              <p className="max-w-[17rem] text-sm leading-relaxed text-ink-secondary font-bold opacity-80">{t.pricing.plans.basic.desc}</p>
            </div>

            <div className="min-h-[140px] py-1 flex flex-col justify-start">
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-serif text-brand-900/35">{regionCurrency}</span>
                <span className="text-6xl font-serif font-bold text-brand-900">{regionPrices.free}</span>
                <span className="ml-1 text-xs font-bold uppercase tracking-widest text-ink-muted">/mo</span>
              </div>
            </div>

            <div className="mb-8 flex-1 border-t border-surface-divider pt-6">
              <ul className="space-y-3.5">
                {t.pricing.features.basic.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3.5 text-sm font-medium text-brand-900 leading-relaxed">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface text-ink-muted transition-colors group-hover:bg-surface-hover group-hover:text-brand-900">
                      <Check size={12} strokeWidth={3} />
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-auto border-t border-surface-divider pt-6">
              <button
                onClick={onSignUp}
                className="h-14 w-full rounded-2xl border border-surface-border bg-surface text-sm font-bold text-brand-900 transition-all hover:bg-white hover:shadow-md active:scale-[0.98]"
              >
                {t.pricing.getStarted}
              </button>
            </div>
          </div>

          <div className="relative z-10 flex flex-col overflow-visible rounded-pricing border border-brand-300/30 bg-white/95 p-8 lg:p-9 shadow-card-hover ring-1 ring-brand-200/40 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1">
            <div className="absolute inset-0 rounded-pricing bg-gradient-to-b from-brand-50/70 via-white/90 to-prestige-50/40 pointer-events-none"></div>
            <DraggablePopularTag label={t.pricing.mostPopular} className="top-5 right-8" />

            <div className="relative min-h-[130px] pr-10 space-y-2">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-700/40">Professional</div>
              <h3 className="text-4xl font-serif font-bold text-brand-900">{t.pricing.plans.pro.name}</h3>
              <p className="max-w-[17rem] text-sm leading-relaxed text-brand-700 font-bold opacity-80">{t.pricing.plans.pro.desc}</p>
            </div>

            <div className="relative min-h-[140px] py-1 flex flex-col justify-start gap-2 text-brand-900">
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-serif font-bold opacity-35">{regionCurrency}</span>
                <span className="text-6xl font-serif font-bold tracking-tighter">{isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly}</span>
                <span className="ml-1 text-xs font-bold uppercase tracking-widest text-brand-700">/mo</span>
              </div>
              {isAnnual && (
                <div className="flex items-baseline gap-1.5 text-[11px] font-semibold tracking-tight leading-relaxed text-ink-muted/60">
                  <span className="line-through">{regionCurrency}{regionPrices.pro.monthly}</span>
                  <span>,</span>
                  <span>{t.pricing.annualSaveTag}</span>
                </div>
              )}
              <div className="text-[11px] font-semibold leading-relaxed text-brand-700/70">
                {calculatePriceAnchor()}
              </div>
            </div>

            <div className="relative mb-8 flex-1 border-t border-brand-200/60 pt-6">
              <ul className="space-y-3.5">
                {t.pricing.features.pro.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3.5 text-sm font-medium text-brand-900 leading-relaxed">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                      <Check size={12} strokeWidth={3} />
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mt-auto border-t border-brand-200/60 pt-6">
              <button
                onClick={() => handlePurchaseClick('membership', 'pro')}
                disabled={purchasing === 'pro'}
                className="group/btn relative h-14 w-full overflow-hidden rounded-2xl bg-brand-600 text-base font-bold text-surface-light shadow-btn-green transition-all hover:-translate-y-0.5 hover:bg-brand-700 active:scale-[0.98]"
              >
                <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                <span className="relative z-10 flex items-center justify-center gap-3">
                  {purchasing === 'pro' ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t.paymentModal.processing}
                    </>
                  ) : (
                    <>
                      {t.paymentModal.buyNow}
                      <ArrowRight size={18} className="transition-transform group-hover/btn:translate-x-1 stroke-[2.5]" />
                    </>
                  )}
                </span>
              </button>
            </div>
          </div>

          <div className="group relative flex flex-col overflow-hidden rounded-pricing border border-ink-dark bg-prestige-900 p-8 lg:p-9 text-surface-light shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_40px_80px_-20px_rgba(62,52,48,0.28)]">
            <div className="absolute top-0 right-0 h-48 w-48 rounded-bl-full bg-prestige-950/40 pointer-events-none"></div>
            <div className="absolute top-0 right-0 h-64 w-64 rounded-bl-[160px] bg-prestige-300/5 pointer-events-none"></div>
            <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>

            <div className="relative min-h-[130px] space-y-2">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-prestige-accent">Executive</div>
              <h3 className="text-4xl font-serif font-bold tracking-tight">{t.pricing.plans.team.name}</h3>
              <p className="max-w-[19rem] text-sm leading-relaxed text-white/70 font-bold">{t.pricing.plans.team.desc}</p>
            </div>

            <div className="relative min-h-[140px] py-1 flex flex-col justify-start gap-2">
              <div className="flex items-start pt-1.5 min-h-[60px]">
                <span className="text-[32px] md:text-4xl font-serif font-bold tracking-tight text-prestige-accent leading-none">
                  {t.paymentModal.customPlan}
                </span>
              </div>
              <p className="text-[11px] font-semibold leading-relaxed text-white/60 max-w-[16rem] mt-1">
                {t.paymentModal.customPlanDesc}
              </p>
            </div>

            <div className="relative mb-8 flex-1 border-t border-white/10 pt-6">
              <ul className="space-y-3.5">
                {t.pricing.features.team.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3.5 text-sm font-medium text-white/90 leading-relaxed">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-prestige-300">
                      <Check size={16} className="stroke-[3]" />
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mt-auto border-t border-white/10 pt-6">
              <button
                onClick={() => setShowContactModal(true)}
                className="group/team h-14 w-full rounded-2xl text-base font-bold transition-all shadow-xl active:scale-[0.98] bg-prestige-300 text-prestige-900 hover:bg-white"
              >
                <span className="flex items-center justify-center gap-3">
                  {t.pricing.contactSales}
                  <ArrowRight size={18} className="transition-transform group-hover/team:translate-x-1 stroke-[2.5]" />
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-20 max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-serif font-bold text-brand-900 mb-2">{t.pricing.addons.title}</h3>
            <p className="text-sm text-ink-secondary font-normal">{t.pricing.addons.desc}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group flex items-center justify-between rounded-modal border border-surface-border bg-white/90 p-6 shadow-sm backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface text-ink-muted transition-colors group-hover:bg-surface-hover group-hover:text-brand-900">
                  <Clock size={24} />
                </div>
                <div>
                  <div className="mb-0.5 text-xs font-bold uppercase tracking-wider text-ink-muted">{t.pricing.addons.single}</div>
                  <div className="text-lg font-bold text-brand-900">{regionCurrency}{regionPrices.addons.single}</div>
                </div>
              </div>
              <button
                onClick={() => handlePurchaseClick('duration', '60min')}
                disabled={purchasing === '60min'}
                className="relative overflow-hidden group/add h-11 w-11 flex items-center justify-center rounded-xl bg-surface border border-surface-border text-brand-900 shadow-sm transition-all hover:bg-brand-900 hover:text-white hover:shadow-md hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover/add:opacity-100 transition-opacity"></div>
                {purchasing === '60min' ? <Loader2 size={18} className="animate-spin" /> : <Plus size={20} strokeWidth={3} className="relative z-10" />}
              </button>
            </div>

            <div className="group relative flex items-center justify-between overflow-hidden rounded-modal border border-prestige-300/35 bg-prestige-50/70 p-6 shadow-sm backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="absolute right-6 top-0 rounded-b-lg bg-prestige-900 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-prestige-300">
                {t.pricing.addons.popular}
              </div>
              <div className="flex items-center gap-4 pr-10">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-prestige-700 shadow-sm">
                  <Coins size={24} />
                </div>
                <div>
                  <div className="mb-0.5 text-xs font-bold uppercase tracking-wider text-prestige-700">{t.pricing.addons.value}</div>
                  <div className="text-lg font-bold text-brand-900">
                    {regionCurrency}{regionPrices.addons.value.price}
                    <span className="ml-1 text-xs text-ink-muted line-through">{regionCurrency}{regionPrices.addons.value.originalPrice}</span>
                  </div>
                  <div className="mt-1 text-[11px] font-semibold text-prestige-700/75">{regionPrices.addons.value.detail}</div>
                </div>
              </div>
              <button
                onClick={() => handlePurchaseClick('duration', '600min')}
                disabled={purchasing === '600min'}
                className="relative overflow-hidden group/add h-11 w-11 flex items-center justify-center rounded-xl bg-prestige-900 text-prestige-300 shadow-cta-dark transition-all hover:-translate-y-0.5 hover:bg-prestige-800 hover:shadow-lg hover:text-white active:scale-95 disabled:opacity-50"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-white/20"></div>
                {purchasing === '600min' ? <Loader2 size={18} className="animate-spin" /> : <Plus size={20} strokeWidth={3} className="relative z-10" />}
              </button>
            </div>

            <div className="group flex items-center justify-between rounded-modal border border-ink-dark bg-prestige-900 p-6 shadow-xl transition-all hover:-translate-y-0.5 hover:shadow-2xl">
              <div className="flex items-center gap-4 text-white">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-prestige-300 backdrop-blur-sm">
                  <Clock size={24} />
                </div>
                <div>
                  <div className="mb-0.5 text-xs font-bold uppercase tracking-wider text-prestige-300/70">{t.pricing.addons.business}</div>
                  <div className="text-lg font-bold text-white">
                    {regionCurrency}{regionPrices.addons.business.price}
                    <span className="ml-1 text-xs text-white/30 line-through">{regionCurrency}{regionPrices.addons.business.originalPrice}</span>
                  </div>
                  <div className="mt-1 text-[11px] font-semibold text-prestige-300/70">{regionPrices.addons.business.detail}</div>
                </div>
              </div>
              <button
                onClick={() => handlePurchaseClick('duration', '3000min')}
                disabled={purchasing === '3000min'}
                className="relative overflow-hidden group/add h-11 w-11 flex items-center justify-center rounded-xl bg-prestige-300 text-prestige-900 shadow-md transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-lg active:scale-95 disabled:opacity-50"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-white/40"></div>
                {purchasing === '3000min' ? <Loader2 size={18} className="animate-spin" /> : <Plus size={20} strokeWidth={3} className="relative z-10" />}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 max-w-5xl mx-auto">
          <div className="relative p-10 lg:p-12 rounded-[2rem] bg-white border border-surface-divider shadow-sm overflow-hidden flex flex-col md:flex-row items-center gap-12 group hover:shadow-md transition-all duration-500">
            <div className="absolute inset-0 bg-noise opacity-[0.02] pointer-events-none"></div>

            <div className="relative z-10 flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-4 ring-white shadow-sm transition-transform group-hover:scale-110 duration-500">
                  <Sparkles size={22} strokeWidth={2.5} />
                </div>
                <h4 className="text-2xl font-serif font-bold text-brand-900">{t.pricing.trustTitle}</h4>
              </div>
              <p className="text-[15px] text-ink-secondary/90 font-medium leading-relaxed max-w-lg">
                {t.pricing.trustDesc}
              </p>
            </div>

            <div className="relative z-10 flex flex-wrap gap-8 items-center shrink-0 border-l border-surface-divider pl-10 md:pl-12">
              <div className="group/stat text-left">
                <div className="text-3xl font-serif font-bold text-brand-900 flex items-baseline gap-1">
                  99.9<span className="text-brand-700/60 text-xl">%</span>
                </div>
                <div className="text-[10px] font-black text-brand-700/80 uppercase tracking-widest mt-1.5 transition-colors group-hover/stat:text-brand-900">
                  {t.paymentModal.accuracyGuaranteed}
                </div>
              </div>
              <div className="group/stat text-left">
                <div className="text-3xl font-serif font-bold text-brand-900 flex items-baseline">
                  {t.paymentModal.bankLevelSec}
                </div>
                <div className="text-[10px] font-black text-brand-700/80 uppercase tracking-widest mt-1.5 transition-colors group-hover/stat:text-brand-900">
                  {t.paymentModal.bankLevelSecSub}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Team 联系我们弹窗 */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
          {/* Backdrop: Premium Light Glassmorphism */}
          <div
            className="absolute inset-0 bg-white/35 backdrop-blur-[8px] transform-gpu transition-opacity duration-300 pointer-events-auto"
            style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
            onClick={() => setShowContactModal(false)}
          >
            {/* Subtle radial light focus */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(255,255,255,0.25)_100%)] pointer-events-none"></div>
          </div>
          <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-modal-heavy border border-white overflow-hidden animate-in zoom-in-95 duration-300 z-10">
            <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-prestige-300/10 rounded-bl-[120px] pointer-events-none"></div>

            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>

            <div className="p-10 relative z-10">
              <div className="w-16 h-16 bg-prestige-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Mail size={28} className="text-prestige-300" />
              </div>

              <h3 className="text-2xl font-serif font-bold text-brand-900 text-center mb-2">
                {t.paymentModal.contactTitle}
              </h3>
              <p className="text-sm text-ink-secondary text-center mb-8 leading-relaxed">
                {t.paymentModal.contactDesc}
              </p>

              <div className="bg-surface rounded-2xl border border-surface-border p-5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-white rounded-xl border border-surface-border flex items-center justify-center shrink-0 shadow-sm">
                    <Mail size={18} className="text-ink-secondary" />
                  </div>
                  <span className="text-sm font-mono font-bold text-brand-900 truncate">
                    support@minutes-ai.com
                  </span>
                </div>
                <button
                  onClick={() => {
                    copyToClipboard('support@minutes-ai.com');
                    setCopied(true);
                    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
                    copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
                  }}
                  className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${copied
                    ? 'bg-brand-500 text-white'
                    : 'bg-brand-900 text-white hover:bg-brand-800'
                    }`}
                >
                  {copied ? t.paymentModal.copied : t.paymentModal.copy}
                </button>
              </div>

              <button
                onClick={() => setShowContactModal(false)}
                className="w-full mt-6 py-3 rounded-xl text-sm font-bold text-ink-secondary bg-white border border-surface-border hover:bg-surface transition-all"
              >
                {t.paymentModal.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 地区检测 + 支付方式选择弹窗 */}
      {showRegionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeRegionModal}
          ></div>
          <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={closeRegionModal}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>

            <div className="p-10">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Globe size={28} className="text-brand-700" />
              </div>

              <h3 className="text-2xl font-serif font-bold text-brand-900 text-center mb-2">
                {t.paymentModal.chooseMethod}
              </h3>

              {regionLoading ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 size={32} className="animate-spin text-brand-700" />
                  <p className="text-sm text-ink-secondary">{t.paymentModal.detectingRegion}</p>
                </div>
              ) : regionInfo ? (
                <div className="space-y-4">
                  <div className="bg-surface rounded-2xl border border-surface-border p-4">
                    <div className="flex items-center gap-2 text-xs text-ink-secondary">
                      <MapPin size={14} className="text-brand-700" />
                      <span>{regionInfo.region === 'CN' ? t.paymentModal.regionMainland : t.paymentModal.regionOverseas}</span>
                      {regionInfo.city && <span>· {regionInfo.city}</span>}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs font-bold text-brand-700 mb-1">
                      {t.paymentModal.selectMethod}
                    </div>
                    {regionInfo.availablePaymentMethods.filter(m => m === 'wechat' || m === 'alipay').map((m) => (
                      <button
                        key={m}
                        onClick={() => setSelectedPaymentMethod(m)}
                        className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${selectedPaymentMethod === m
                          ? 'border-brand-700 bg-brand-50'
                          : 'border-surface-border bg-white hover:border-brand-200'
                          }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold ${m === 'wechat' ? 'bg-brand-50 text-brand-700' : 'bg-prestige-50 text-prestige-700'
                          }`}>
                          {m === 'wechat' ? '微' : '支'}
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-bold text-brand-900">
                            {m === 'wechat' ? t.paymentModal.wechatPay : t.paymentModal.alipay}
                          </div>
                          <div className="text-xs text-ink-secondary">
                            {m === 'wechat' ? t.paymentModal.wechatDesc : t.paymentModal.alipayDesc}
                          </div>
                        </div>
                        <div className="ml-auto">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === m ? 'border-brand-700 bg-brand-700' : 'border-ink-border'
                            }`}>
                            {selectedPaymentMethod === m && <Check size={12} className="text-white" />}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-ink-secondary">
                  {t.paymentModal.detectFailed}
                </div>
              )}

              <button
                onClick={handleRegionConfirm}
                disabled={regionLoading || !selectedPaymentMethod}
                className="w-full mt-6 h-12 rounded-xl font-bold text-sm bg-brand-900 text-white hover:bg-brand-800 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regionLoading ? t.paymentModal.detecting : t.paymentModal.confirmPayment}
              </button>

              <button
                onClick={closeRegionModal}
                className="w-full mt-3 py-3 rounded-xl text-sm font-bold text-ink-secondary bg-white border border-surface-border hover:bg-surface transition-all"
              >
                {t.paymentModal.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 二维码支付弹窗 */}
      {showQrModal && qrCodeUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowQrModal(false)}
          ></div>
          <div className="relative w-full max-w-sm bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={() => setShowQrModal(false)}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>

            <div className="p-10 text-center">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg text-2xl font-bold ${qrPaymentType?.includes('wechat') ? 'bg-brand-50 text-brand-700' : 'bg-prestige-50 text-prestige-700'
                }`}>
                {qrPaymentType?.includes('wechat') ? '微' : '支'}
              </div>

              <h3 className="text-xl font-serif font-bold text-brand-900 mb-2">
                {qrPaymentType?.includes('wechat') ? t.paymentModal.wechatScanPay : t.paymentModal.alipayScanPay}
              </h3>

              <p className="text-xs text-ink-secondary mb-6">
                {t.paymentModal.qrScanScan}
              </p>

              <div className="bg-white border-2 border-surface-border rounded-2xl p-4 inline-block mb-4">
                <QRCodeSVG value={qrCodeUrl} size={200} />
              </div>

              {qrOrderNo && (
                <p className="text-xs text-ink-muted mb-4">
                  {t.paymentModal.qrOrderNo}{qrOrderNo}
                </p>
              )}

              <p className="text-xs text-ink-secondary">
                {t.paymentModal.qrUpdateAuto}
              </p>

              <button
                onClick={() => {
                  setShowQrModal(false);
                  setQrCodeUrl(null);
                }}
                className="w-full mt-6 h-12 rounded-xl font-bold text-sm bg-brand-900 text-white hover:bg-brand-800 transition-all active:scale-[0.98]"
              >
                {t.paymentModal.qrFinished}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

